import { asInstanceOf, Option, Terminable, Terminator, tryCatch, UUID } from "@opendaw/lib-std";
import { AudioUtils } from "../AudioUtils";
import { PPQN, TimeBase } from "@opendaw/lib-dsp";
import { AudioFileBox, AudioRegionBox, TrackBox, ValueEventCollectionBox } from "@opendaw/studio-boxes";
import { ColorCodes, TrackType, UnionBoxTypes } from "@opendaw/studio-adapters";
import { Recording } from "./Recording";
import { RecordTrack } from "./RecordTrack";
export var RecordAudio;
(function (RecordAudio) {
    RecordAudio.start = ({ recordingWorklet, sourceNode, sampleManager, project, capture, outputLatency, inputLatency }) => {
        console.debug("[RecordAudio] start", { outputLatency, inputLatency });
        const terminator = new Terminator();
        const beats = PPQN.fromSignature(1, project.timelineBox.signature.denominator.getValue());
        const { editing, engine, boxGraph, timelineBox, tempoMap } = project;
        const originalUuid = recordingWorklet.uuid;
        // Note: sampleManager.record() and sourceNode.connect() are called in prepareRecording
        let fileBox = Option.None;
        let currentTake = Option.None;
        let lastPosition = 0;
        let currentWaveformOffset = outputLatency + inputLatency;
        let takeNumber = 0;
        const allTakeRegions = [];
        let initialWaveformOffset = 0;
        const { env: { audioContext: { sampleRate } }, engine: { preferences: { settings: { recording } } } } = project;
        const { loopArea } = timelineBox;
        const createFileBox = () => {
            const fileDateString = new Date()
                .toISOString()
                .replaceAll("T", "-")
                .replaceAll(".", "-")
                .replaceAll(":", "-")
                .replaceAll("Z", "");
            const fileName = `Recording-${fileDateString}`;
            return AudioFileBox.create(boxGraph, originalUuid, box => box.fileName.setValue(fileName));
        };
        const createTakeRegion = (position, waveformOffset, excludeTrack) => {
            takeNumber++;
            console.debug("[RecordAudio] createTakeRegion", { takeNumber, position, waveformOffset });
            const overwrite = recording.overwriteOnRecord;
            const trackBox = RecordTrack.findOrCreate(editing, capture.audioUnitBox, TrackType.Audio, excludeTrack, overwrite);
            const preExistingRegions = overwrite
                ? [...trackBox.regions.pointerHub.incoming().map(({ box }) => asInstanceOf(box, AudioRegionBox))]
                : [];
            const collectionBox = ValueEventCollectionBox.create(boxGraph, UUID.generate());
            const regionBox = AudioRegionBox.create(boxGraph, UUID.generate(), box => {
                box.file.refer(fileBox.unwrap("fileBox"));
                box.events.refer(collectionBox.owners);
                box.regions.refer(trackBox.regions);
                box.position.setValue(position);
                box.hue.setValue(ColorCodes.forTrackType(TrackType.Audio));
                box.timeBase.setValue(TimeBase.Seconds);
                box.label.setValue(`Take ${takeNumber}`);
                box.waveformOffset.setValue(waveformOffset);
            });
            capture.addRecordedRegion(regionBox);
            allTakeRegions.push(regionBox);
            project.selection.select(regionBox);
            return { trackBox, regionBox, preExistingRegions };
        };
        const finalizeTake = (take, durationInSeconds) => {
            console.debug("[RecordAudio] finalizeTake", { durationInSeconds });
            const { trackBox, regionBox, preExistingRegions } = take;
            if (regionBox.isAttached()) {
                // The finalized length is recomputed (loop boundary), so unlike the live update it can be
                // non-positive for a take that started at/after loopTo. Never persist a zero/negative
                // duration region (it later trips validateTrack "duration must be positive") — drop it.
                if (durationInSeconds <= 0) {
                    console.debug("[RecordAudio] finalizeTake: dropping non-positive take", { durationInSeconds });
                    regionBox.delete();
                    return;
                }
                regionBox.duration.setValue(durationInSeconds);
                regionBox.loopDuration.setValue(durationInSeconds);
            }
            if (recording.overwriteOnRecord) {
                if (!regionBox.isAttached()) {
                    return;
                }
                const newStart = regionBox.position.getValue();
                const newStartSec = tempoMap.ppqnToSeconds(newStart);
                const newEndSec = newStartSec + durationInSeconds;
                for (const oldRegion of preExistingRegions) {
                    if (!oldRegion.isAttached()) {
                        continue;
                    }
                    const oldStart = oldRegion.position.getValue();
                    const oldStartSec = tempoMap.ppqnToSeconds(oldStart);
                    const oldDurationSec = oldRegion.duration.getValue();
                    const oldEndSec = oldStartSec + oldDurationSec;
                    if (oldEndSec <= newStartSec || oldStartSec >= newEndSec) {
                        continue;
                    }
                    if (oldStartSec >= newStartSec && oldEndSec <= newEndSec) {
                        oldRegion.delete();
                    }
                    else if (oldEndSec <= newEndSec) {
                        const newDuration = newStartSec - oldStartSec;
                        oldRegion.duration.setValue(newDuration);
                        oldRegion.loopDuration.setValue(newDuration);
                    }
                    else if (oldStartSec >= newStartSec) {
                        const trimSec = newEndSec - oldStartSec;
                        oldRegion.position.setValue(tempoMap.secondsToPPQN(newEndSec));
                        oldRegion.waveformOffset.setValue(oldRegion.waveformOffset.getValue() + trimSec);
                        oldRegion.duration.setValue(oldDurationSec - trimSec);
                        oldRegion.loopDuration.setValue(oldDurationSec - trimSec);
                    }
                    else {
                        const newDuration = newStartSec - oldStartSec;
                        oldRegion.duration.setValue(newDuration);
                        oldRegion.loopDuration.setValue(newDuration);
                    }
                }
                return;
            }
            const { olderTakeAction, olderTakeScope } = recording;
            if (olderTakeScope === "none") {
                return;
            }
            if (olderTakeScope === "all") {
                for (const track of capture.audioUnitBox.tracks.pointerHub.incoming()
                    .map(({ box }) => asInstanceOf(box, TrackBox))) {
                    const trackType = track.type.getValue();
                    if (trackType === TrackType.Value || trackType === TrackType.Undefined) {
                        continue;
                    }
                    if (track === trackBox) {
                        continue;
                    }
                    if (olderTakeAction === "disable-track") {
                        if (track.isAttached()) {
                            track.enabled.setValue(false);
                        }
                    }
                    else {
                        for (const region of track.regions.pointerHub.incoming()
                            .map(({ box }) => UnionBoxTypes.asRegionBox(box))) {
                            if (region.isAttached()) {
                                region.mute.setValue(true);
                            }
                        }
                    }
                }
            }
            else {
                if (olderTakeAction === "disable-track") {
                    if (trackBox.isAttached()) {
                        trackBox.enabled.setValue(false);
                    }
                }
                else {
                    if (regionBox.isAttached()) {
                        regionBox.mute.setValue(true);
                    }
                }
            }
        };
        const startNewTake = (position) => {
            const previousTrack = currentTake.mapOr(take => take.trackBox, null);
            currentTake = Option.wrap(createTakeRegion(position, currentWaveformOffset, previousTrack));
        };
        recordingWorklet.onSaved = uuid => {
            project.trackUserCreatedSample(uuid);
            const audioData = recordingWorklet.data.unwrap("Recorded audio data missing");
            fileBox.ifSome(oldFileBox => {
                if (!oldFileBox.isAttached() || oldFileBox.pointerHub.isEmpty()) {
                    return;
                }
                editing.modify(() => {
                    const incomingPointers = [...oldFileBox.pointerHub.incoming()];
                    const incomingTransientPointers = [...oldFileBox.transientMarkers.pointerHub.incoming()];
                    if (incomingPointers.length === 0) {
                        oldFileBox.delete();
                        return;
                    }
                    // endInSeconds must reflect the *imported sample's* actual
                    // frame count, not the live `numberOfFrames` snapshot kept
                    // on `oldFileBox`. The worklet's ring buffer can overshoot
                    // the recording `limit` by up to one quantum before
                    // `#finalize` truncates it, so oldFileBox.endInSeconds is
                    // inflated. Copying it would stretch the rendered waveform
                    // by that overshoot, causing a linear visual drift along
                    // the file (audio playback is unaffected).
                    const newFileBox = AudioFileBox.create(boxGraph, uuid, box => {
                        box.fileName.setValue(oldFileBox.fileName.getValue());
                        box.startInSeconds.setValue(0);
                        box.endInSeconds.setValue(audioData.numberOfFrames / audioData.sampleRate);
                    });
                    for (const pointer of incomingPointers) {
                        pointer.refer(newFileBox);
                    }
                    for (const pointer of incomingTransientPointers) {
                        pointer.refer(newFileBox.transientMarkers);
                    }
                    oldFileBox.delete();
                }, false);
            });
            if (allTakeRegions.length > 0) {
                const startFrame = Math.floor(initialWaveformOffset * audioData.sampleRate);
                const firstNonSilent = AudioUtils.findFirstNonSilentSample(audioData.frames, startFrame);
                const silenceFrames = firstNonSilent - startFrame;
                if (silenceFrames > 0) {
                    const silenceSeconds = silenceFrames / audioData.sampleRate;
                    editing.modify(() => {
                        for (const regionBox of allTakeRegions) {
                            if (!regionBox.isAttached()) {
                                continue;
                            }
                            regionBox.waveformOffset.setValue(regionBox.waveformOffset.getValue() + silenceSeconds);
                            const pos = regionBox.position.getValue();
                            const posSeconds = tempoMap.ppqnToSeconds(pos);
                            const silencePPQN = tempoMap.intervalToPPQN(posSeconds, posSeconds + silenceSeconds);
                            regionBox.position.setValue(Math.max(0, pos - silencePPQN));
                        }
                        const lastRegion = allTakeRegions.at(-1);
                        if (lastRegion?.isAttached()) {
                            const newDuration = Math.max(0, lastRegion.duration.getValue() - silenceSeconds);
                            lastRegion.duration.setValue(newDuration);
                            lastRegion.loopDuration.setValue(newDuration);
                        }
                    }, false);
                }
            }
            editing.mark();
        };
        terminator.ownAll(Terminable.create(() => {
            tryCatch(() => sourceNode.disconnect(recordingWorklet));
            if (recordingWorklet.numberOfFrames === 0 || fileBox.isEmpty()) {
                console.debug("[RecordAudio] abort", {
                    numberOfFrames: recordingWorklet.numberOfFrames,
                    hasFile: fileBox.nonEmpty()
                });
                sampleManager.remove(originalUuid);
                recordingWorklet.terminate();
            }
            else {
                // fixes #840: short recordings (e.g. count-in) can leave zero-duration regions
                currentTake.ifSome(take => {
                    const { regionBox } = take;
                    const duration = regionBox.duration.getValue();
                    if (duration <= 0) {
                        console.debug("[RecordAudio] stop: deleting zero-duration region", { takeNumber });
                        editing.modify(() => regionBox.delete(), false);
                    }
                    else {
                        console.debug("[RecordAudio] stop", {
                            takeNumber,
                            duration,
                            numberOfFrames: recordingWorklet.numberOfFrames
                        });
                        recordingWorklet.limit(Math.ceil((currentWaveformOffset + duration) * sampleRate));
                        if (recording.overwriteOnRecord) {
                            editing.modify(() => finalizeTake(take, duration), false);
                        }
                    }
                });
                fileBox.ifSome(box => {
                    if (box.isAttached()) {
                        box.endInSeconds.setValue(recordingWorklet.numberOfFrames / sampleRate);
                    }
                });
            }
        }), engine.position.catchupAndSubscribe(owner => {
            const isCountingIn = engine.isCountingIn.getValue();
            const isRecording = engine.isRecording.getValue();
            if (!isCountingIn && !isRecording) {
                return;
            }
            const currentPosition = owner.getValue();
            if (isCountingIn) {
                return;
            }
            // From here on, isRecording is true
            const loopEnabled = loopArea.enabled.getValue();
            const loopFrom = loopArea.from.getValue();
            const { allowTakes, overwriteOnRecord } = project.engine.preferences.settings.recording;
            if (loopEnabled && allowTakes && !overwriteOnRecord && currentTake.nonEmpty() && currentPosition < lastPosition) {
                // Compute the take's length from its own start position to
                // loopTo, not from loopFrom. When recording begins mid-loop,
                // the first take only spans [takePosition, loopTo]; using
                // the full loop length would overshoot by (takePosition - loopFrom).
                // Stays deterministic (avoids the latency-lagged live
                // regionBox.duration that previously caused peak drift):
                // subsequent takes start at loopFrom, so this evaluates to
                // the full loop length exactly as before.
                const loopTo = loopArea.to.getValue();
                editing.modify(() => {
                    currentTake.ifSome(take => {
                        if (take.regionBox.duration.getValue() <= 0) {
                            take.regionBox.delete();
                            currentTake = Option.None;
                            return;
                        }
                        const takeDurationSeconds = tempoMap.intervalToSeconds(take.regionBox.position.getValue(), loopTo);
                        finalizeTake(take, takeDurationSeconds);
                        currentWaveformOffset += takeDurationSeconds;
                    });
                    if (currentTake.nonEmpty()) {
                        startNewTake(loopFrom);
                    }
                }, false);
            }
            lastPosition = currentPosition;
            // Create fileBox and region together when recording starts.
            if (fileBox.isEmpty()) {
                // Three terms make up the file_time of audio captured at engine timeline
                // = currentPosition (mic case):
                //   L                  — worklet head-start: wall-clock between the worklet
                //                        being connected (in prepareRecording) and the engine
                //                        actually beginning count-in. Cannot be derived from
                //                        BPM/signature alone.
                //   countInSeconds     — deterministic from bars × signature × bpm.
                //   outputLatency      — engine→speaker compensation.
                //   inputLatency       — mic→worklet compensation (from MediaStreamTrack.getSettings().latency
                //                        with audioContext.inputLatency as fallback).
                // L is recovered once, here, by reading numberOfFrames at the moment we first
                // see isRecording=true and subtracting the BPM-derived countInSeconds. Earlier
                // attempts that omitted L put a systematic ~30 ms (one render dispatch) bias
                // on every take; earlier attempts that used numberOfFrames as the whole offset
                // were noisy by the same ~few ms but never wildly wrong.
                const countedIn = Recording.wasCountingIn();
                const barPPQN = PPQN.fromSignature(timelineBox.signature.nominator.getValue(), timelineBox.signature.denominator.getValue());
                const countInSeconds = countedIn
                    ? PPQN.pulsesToSeconds(recording.countInBars * barPPQN, timelineBox.bpm.getValue())
                    : 0;
                const wallclockSinceWorklet = recordingWorklet.numberOfFrames / sampleRate;
                const headStartSeconds = countedIn
                    ? Math.max(0, wallclockSinceWorklet - countInSeconds)
                    : wallclockSinceWorklet;
                const waveformOffset = headStartSeconds + countInSeconds + outputLatency + inputLatency;
                editing.modify(() => {
                    fileBox = Option.wrap(createFileBox());
                    currentTake = Option.wrap(createTakeRegion(currentPosition, waveformOffset, null));
                }, false);
                currentWaveformOffset = waveformOffset;
                initialWaveformOffset = waveformOffset;
            }
            currentTake.ifSome(({ regionBox }) => editing.modify(() => {
                if (regionBox.isAttached()) {
                    const { duration, loopDuration } = regionBox;
                    const totalSeconds = recordingWorklet.numberOfFrames / sampleRate;
                    const takeSeconds = totalSeconds - currentWaveformOffset;
                    duration.setValue(takeSeconds);
                    loopDuration.setValue(takeSeconds);
                    recordingWorklet.setFillLength(recordingWorklet.numberOfFrames);
                    fileBox.ifSome(box => box.endInSeconds.setValue(totalSeconds));
                }
                else {
                    terminator.terminate();
                    currentTake = Option.None;
                }
            }, false));
        }));
        return terminator;
    };
})(RecordAudio || (RecordAudio = {}));
